import { SWF_STAGE_HEIGHT, SWF_STAGE_WIDTH } from "./swfBattleStage";

/**
 * BATTLE_STAGE_PADDING，供本模块相关规则使用。
 */
export const BATTLE_STAGE_PADDING = 40;
/**
 * MIN_BATTLE_STAGE_ZOOM，供本模块相关规则使用。
 */
export const MIN_BATTLE_STAGE_ZOOM = 0.1;
/**
 * MAX_BATTLE_STAGE_ZOOM，供本模块相关规则使用。
 */
export const MAX_BATTLE_STAGE_ZOOM = 16;
/**
 * DEFAULT_BATTLE_STAGE_ZOOM，供本模块相关规则使用。
 */
export const DEFAULT_BATTLE_STAGE_ZOOM = 0.8;
/**
 * FIT_BATTLE_STAGE_ZOOM，供本模块相关规则使用。
 */
export const FIT_BATTLE_STAGE_ZOOM = 1;

/**
 * BattleStageViewportSize 的数据结构或可选取值约定。
 */
export interface BattleStageViewportSize {
    /**
     * width，保存当前实例的width状态。
     */
    width: number;
    /**
     * height，保存当前实例的height状态。
     */
    height: number;
}

/**
 * BattleStagePoint 的数据结构或可选取值约定。
 */
export interface BattleStagePoint {
    /**
     * x，保存当前实例的x状态。
     */
    x: number;
    /**
     * y，保存当前实例的y状态。
     */
    y: number;
}

/** Bounds are expressed in the original SWF stage coordinate system. */
export interface BattleStageBounds {
    /**
     * minX，保存当前实例的minX状态。
     */
    minX: number;
    /**
     * maxX，保存当前实例的maxX状态。
     */
    maxX: number;
    /**
     * minY，保存当前实例的minY状态。
     */
    minY: number;
    /**
     * maxY，保存当前实例的maxY状态。
     */
    maxY: number;
}

/**
 * The render camera shared by overlaid battle animations. Battle mode keeps
 * x/y at the fixed 960x560 stage centre while scale may reveal coordinates
 * outside that frame.
 */
export interface BattleStageRenderViewport extends BattleStageViewportSize {
    /** Stage coordinate displayed at the centre of the browser viewport. */
    x: number;
    /**
     * y，保存当前实例的y状态。
     */
    y: number;
    /**
     * scale，保存当前实例的scale状态。
     */
    scale: number;
}

/**
 * DEFAULT_BATTLE_STAGE_BOUNDS，供本模块相关规则使用。
 */
export const DEFAULT_BATTLE_STAGE_BOUNDS: BattleStageBounds = {
    minX: 0,
    maxX: SWF_STAGE_WIDTH,
    minY: 0,
    maxY: SWF_STAGE_HEIGHT,
};

/**
 * finiteBounds：执行对应的领域操作。
 * @param value 待处理的值
 * @returns 本次操作的结果；具体取值由当前状态与分支决定。
 */
function finiteBounds(value: BattleStageBounds | undefined): value is BattleStageBounds {
    return !!value &&
        Number.isFinite(value.minX) &&
        Number.isFinite(value.maxX) &&
        Number.isFinite(value.minY) &&
        Number.isFinite(value.maxY) &&
        value.maxX >= value.minX &&
        value.maxY >= value.minY;
}

/** Merge the visible bounds of both overlaid battle animations. */
export function unionBattleStageBounds(
    bounds: Iterable<BattleStageBounds | undefined>,
): BattleStageBounds {
    /**
     * result，供本模块相关规则使用。
     */
    let result: BattleStageBounds | undefined;
    for (const candidate of bounds) {
        if (!finiteBounds(candidate)) continue;
        if (!result) {
            result = { ...candidate };
            continue;
        }
        result.minX = Math.min(result.minX, candidate.minX);
        result.maxX = Math.max(result.maxX, candidate.maxX);
        result.minY = Math.min(result.minY, candidate.minY);
        result.maxY = Math.max(result.maxY, candidate.maxY);
    }
    return result ?? { ...DEFAULT_BATTLE_STAGE_BOUNDS };
}

/**
 * battleStageBoundsSize：执行对应的领域操作。
 * @param bounds bounds参数，参与本次操作
 * @returns 本次操作的结果；具体取值由当前状态与分支决定。
 */
export function battleStageBoundsSize(bounds: BattleStageBounds): BattleStageViewportSize {
    return {
        width: Math.max(1, bounds.maxX - bounds.minX),
        height: Math.max(1, bounds.maxY - bounds.minY),
    };
}

/**
 * battleStageBoundsCenter：执行对应的领域操作。
 * @param bounds bounds参数，参与本次操作
 * @returns 本次操作的结果；具体取值由当前状态与分支决定。
 */
export function battleStageBoundsCenter(bounds: BattleStageBounds) {
    return {
        x: (bounds.minX + bounds.maxX) / 2,
        y: (bounds.minY + bounds.maxY) / 2,
    };
}

/**
 * Scale the common battle scene into the available window. The source mesh
 * bounds may extend beyond the 960x560 SWF frame, so fitting is intentionally
 * based on the complete overlaid scene rather than the original frame box.
 */
export function calculateBattleStageFitScale(
    viewport: BattleStageViewportSize,
    bounds: BattleStageBounds = DEFAULT_BATTLE_STAGE_BOUNDS,
    padding = BATTLE_STAGE_PADDING,
    cameraCenter?: BattleStagePoint,
): number {
    /**
     * width，供本模块相关规则使用。
     */
    const width = Number.isFinite(viewport.width) ? viewport.width : 0;
    /**
     * height，供本模块相关规则使用。
     */
    const height = Number.isFinite(viewport.height) ? viewport.height : 0;
    /**
     * availableWidth，供本模块相关规则使用。
     */
    const availableWidth = Math.max(1, width - padding * 2);
    /**
     * availableHeight，供本模块相关规则使用。
     */
    const availableHeight = Math.max(1, height - padding * 2);
    // The shared battle renderer keeps its camera at the canonical stage
    // centre. When bounds are asymmetric (common for attack effects outside
    // the 960x560 frame), fitting by width/height alone would clip one side.
    // Callers that keep a fixed camera can opt into the symmetric extent;
    // existing callers without a centre retain the ordinary bounds fit.
    /**
     * center，供本模块相关规则使用。
     */
    const center = cameraCenter &&
        Number.isFinite(cameraCenter.x) &&
        Number.isFinite(cameraCenter.y)
        ? cameraCenter
        : undefined;
    /**
     * content，供本模块相关规则使用。
     */
    const content = center
        ? {
              width: Math.max(
                  1,
                  2 * Math.max(
                      Math.abs(bounds.minX - center.x),
                      Math.abs(bounds.maxX - center.x),
                  ),
              ),
              height: Math.max(
                  1,
                  2 * Math.max(
                      Math.abs(bounds.minY - center.y),
                      Math.abs(bounds.maxY - center.y),
                  ),
              ),
          }
        : battleStageBoundsSize(bounds);
    return Math.min(
        availableWidth / content.width,
        availableHeight / content.height,
    );
}

/**
 * Return the zoom multiplier needed to fit overflow bounds while keeping 1x
 * tied to the canonical 960x560 stage. The caller still applies the normal
 * zoom limits; this helper only compares the two logical fit scales.
 */
export function calculateBattleStageFitZoom(
    viewport: BattleStageViewportSize,
    bounds: BattleStageBounds = DEFAULT_BATTLE_STAGE_BOUNDS,
    padding = BATTLE_STAGE_PADDING,
    cameraCenter: BattleStagePoint = {
        x: SWF_STAGE_WIDTH / 2,
        y: SWF_STAGE_HEIGHT / 2,
    },
): number {
    /**
     * canonicalScale，供本模块相关规则使用。
     */
    const canonicalScale = calculateBattleStageFitScale(
        viewport,
        DEFAULT_BATTLE_STAGE_BOUNDS,
        padding,
    );
    if (!Number.isFinite(canonicalScale) || canonicalScale <= 0) return 1;
    /**
     * overflowScale，供本模块相关规则使用。
     */
    const overflowScale = calculateBattleStageFitScale(
        viewport,
        bounds,
        padding,
        cameraCenter,
    );
    if (!Number.isFinite(overflowScale) || overflowScale <= 0) return 1;
    return Math.min(FIT_BATTLE_STAGE_ZOOM, overflowScale / canonicalScale);
}

/**
 * clampBattleStageZoom：执行对应的领域操作。
 * @param value 待处理的值
 * @returns 本次操作的结果；具体取值由当前状态与分支决定。
 */
export function clampBattleStageZoom(value: number): number {
    if (!Number.isFinite(value)) return 1;
    return Math.max(MIN_BATTLE_STAGE_ZOOM, Math.min(MAX_BATTLE_STAGE_ZOOM, value));
}
